import { DropdownChangeParams } from 'primereact/dropdown'
import { useCallback, useEffect, useReducer, useState } from 'react'
import { useTranslation } from 'react-i18next'

// local imports
import DashboardService from 'service/DashboardService'
import { IAllFirmList } from 'types/FirmType'
import {
  ChartItems,
  IOnCampusCoiffeurServiceAllFirmsStatistics,
  IOnCampusCoiffeurServiceFirmStatistics,
} from 'types/DashboardType'
import DateUtil from 'utils/DateUtil'
import { roundPercentages } from 'utils/Miscellaneous'

interface IChartState {
  colors: string[]
  items: ChartItems | undefined
  labels: string[]
  footerTitle: string
  detailTitle: string
}

type ActionType = { type: string; payload: { items: ChartItems } }

const chartColors = ['#3C4D9E', '#ADBBFF']

/* Reducer */
const initialState: IChartState = {
  items: undefined,
  colors: chartColors,
  labels: ['dashboard.on-campus-tabs.female-res', 'dashboard.on-campus-tabs.male-res'],
  footerTitle: 'dashboard.on-campus-tabs.all-coiffeur-footer-msg',
  detailTitle: 'dashboard.on-campus-tabs.all-coiffeur-detail-msg',
}

function chartReducer(state: IChartState = initialState, action: ActionType) {
  const { type, payload } = action
  switch (type) {
    case 'all':
      return { ...initialState, ...payload }
    case 'female':
      return {
        ...state,
        items: payload.items,
        colors: [chartColors[0]],
        labels: ['dashboard.on-campus-tabs.female-res'],
        detailTitle: 'dashboard.on-campus-tabs.female-detail-msg',
        footerTitle: 'dashboard.on-campus-tabs.female-footer-msg',
      }
    case 'male':
      return {
        ...state,
        items: payload.items,
        colors: [chartColors[1]],
        labels: ['dashboard.on-campus-tabs.male-res'],
        detailTitle: 'dashboard.on-campus-tabs.male-detail-msg',
        footerTitle: 'dashboard.on-campus-tabs.male-footer-msg',
      }
    default:
      return initialState
  }
}

const fetchSumOfKey = (arr: any[], property: string): number => {
  return arr.map((item: any) => item[property]).reduce((prev: any, curr: any) => prev + curr, 0)
}

const percentage = (partialValue: number, total: number) => (100 * partialValue) / total

const useGetCoiffeurServiceChartData = (props: any) => {
  const { t } = useTranslation()

  const chartTypeOptions = [
    { key: 'all', value: t('dashboard.on-campus-tabs.all-type') },
    { key: 'female', value: t('dashboard.on-campus-tabs.female-type') },
    { key: 'male', value: t('dashboard.on-campus-tabs.male-type') },
  ]

  const [{ items, colors, labels, detailTitle, footerTitle }, dispatch] = useReducer(chartReducer, initialState)
  const [chartData, setChartData] = useState<IOnCampusCoiffeurServiceAllFirmsStatistics | undefined>()
  const [chartType, setChartType] = useState('all')
  const [loading, setLoading] = useState(false)
  const [excelData, setExcelData] = useState<any>()

  const chooseChartDataWithType = (
    selectedType: string,
    data: IOnCampusCoiffeurServiceAllFirmsStatistics | undefined
  ) => {
    if (!data) return

    const femaleCount = data.firmStatistics.map((i: IOnCampusCoiffeurServiceFirmStatistics) => i.femaleCount)
    const maleCount = data.firmStatistics.map((i: IOnCampusCoiffeurServiceFirmStatistics) => i.maleCount)

    const femalePercent = data.firmStatistics.map((i: IOnCampusCoiffeurServiceFirmStatistics) => {
      return percentage(i.femaleCount, i.totalCount)
    })
    const malePercent = data.firmStatistics.map((i: IOnCampusCoiffeurServiceFirmStatistics) => {
      return percentage(i.maleCount, i.totalCount)
    })

    if (selectedType === 'all') {
      const payload = {
        items: {
          doughnutItems: {
            datasets: [
              fetchSumOfKey(data.firmStatistics, 'FemaleCount'),
              fetchSumOfKey(data.firmStatistics, 'MaleCount'),
            ],
            total: fetchSumOfKey(data.firmStatistics, 'TotalCount'),
            totalPercentage: 0,
          },
          stackedItems: {
            labels: data.firmStatistics.map((item: IOnCampusCoiffeurServiceFirmStatistics) => item.firmName),
            count: [femaleCount, maleCount],
            percentage: [roundPercentages(femalePercent), roundPercentages(malePercent)],
          },
        },
      }
      dispatch({ type: 'all', payload })
    } else if (selectedType === 'female') {
      const payload = {
        items: {
          doughnutItems: {
            datasets: [fetchSumOfKey(data.firmStatistics, 'FemaleCount')],
            total: fetchSumOfKey(data.firmStatistics, 'FemaleCount'),
            totalPercentage: 0,
          },
          stackedItems: {
            labels: data.firmStatistics.map((item: IOnCampusCoiffeurServiceFirmStatistics) => item.firmName),
            count: [femaleCount],
            percentage: [roundPercentages(femalePercent)],
          },
        },
      }
      dispatch({ type: 'female', payload })
    } else if (selectedType === 'male') {
      const payload = {
        items: {
          doughnutItems: {
            datasets: [fetchSumOfKey(data.firmStatistics, 'MaleCount')],
            total: fetchSumOfKey(data.firmStatistics, 'MaleCount'),
            totalPercentage: 0,
          },
          stackedItems: {
            labels: data.firmStatistics.map((item: IOnCampusCoiffeurServiceFirmStatistics) => item.firmName),
            count: [maleCount],
            percentage: [roundPercentages(malePercent)],
          },
        },
      }
      dispatch({ type: 'male', payload })
    }
  }

  const getChartData = useCallback(() => {
    let idList = props.selectedFirms.map((item: IAllFirmList) => item.id)
    const request = {
      firmIds: idList,
      startDate: DateUtil.formatDateToString(props.startDate),
      endDate: DateUtil.formatDateToString(props.endDate),
    }
    setLoading(true)

    DashboardService.getCampusCoiffeurServiceStatistics(request)
      .then((resp) => {
        const array = resp.data.firmStatistics.filter((f) => idList.includes(f.firmId))
        chooseChartDataWithType(chartType, { ...resp.data, firmStatistics: array })
        setChartData({ ...resp.data, firmStatistics: array })
        setExcelData(array)
        setLoading(false)
      })
      .catch((err) => console.log(err))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.endDate, props.startDate, props.selectedFirms])

  /* Fetch Statistics */
  useEffect(() => getChartData(), [getChartData])

  /* Chart Type select box handler */
  const handleChartTypeChange = (e: DropdownChangeParams) => {
    const selectedType = e.value
    setChartType(selectedType)
    chooseChartDataWithType(selectedType, chartData)
  }

  return {
    t,
    excelData,
    chartType,
    chartTypeOptions,
    handleChartTypeChange,
    loading,
    items,
    colors,
    labels,
    detailTitle,
    footerTitle,
  }
}

export default useGetCoiffeurServiceChartData
